// Instanced particle pools (§11, §12.9): every particle type shares one sprite atlas, so the whole
// system is two draw calls (normal-blended toon sprites with ink outlines, and additive glows).
// Particles are integrated on the CPU in struct-of-arrays form and uploaded each frame; nothing is
// allocated per frame or per spawn. Budgets follow §12.9 (2000 / 1200 / 600 / 300).
import {
  AdditiveBlending, Color, DynamicDrawUsage, Group, InstancedBufferAttribute, InstancedBufferGeometry, Mesh,
  NormalBlending, PlaneGeometry, ShaderMaterial, type Texture,
} from 'three';
import { ATLAS, Cell } from './atlas';

export const SPRITE_VERTEX = /* glsl */ `
attribute vec3 iPos;
attribute vec3 iVel;
attribute vec4 iShape;   // width, height, rotation, velocity stretch (s)
attribute vec4 iColor;
attribute vec2 iCell;    // atlas cell, horizontal flip/flutter (-1..1)
uniform vec2 uGrid;
varying vec2 vUv;
varying vec4 vColor;
void main() {
  vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
  vec2 c = position.xy;
  float w = iShape.x;
  float h = iShape.y;
  vec2 up = vec2(-sin(iShape.z), cos(iShape.z));
  if (iShape.w > 0.0) {
    vec3 vv = mat3(modelViewMatrix) * iVel;
    float l = length(vv.xy);
    if (l > 0.001) {
      up = vv.xy / l;
      h += l * iShape.w;
    }
  }
  vec2 right = vec2(up.y, -up.x);
  mv.xy += right * (c.x * w * iCell.y) + up * (c.y * h);
  gl_Position = projectionMatrix * mv;
  float col = mod(iCell.x, uGrid.x);
  float row = floor(iCell.x / uGrid.x);
  vUv = vec2((col + uv.x) / uGrid.x, 1.0 - (row + 1.0 - uv.y) / uGrid.y);
  vColor = iColor;
}`;

/** Toon sprite: tint × shade, ink outline, white highlight (atlas channel encoding, atlas.ts). */
export const SPRITE_FRAGMENT = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3 uInk;
uniform float uAdditive;
uniform float uBoost;
varying vec2 vUv;
varying vec4 vColor;
void main() {
  vec4 t = texture2D(uAtlas, vUv);
  float a = t.a * vColor.a;
  if (a < 0.004) discard;
  vec3 col = vColor.rgb * t.r;
  if (uAdditive > 0.5) {
    // HDR: additive sparks and flashes exceed 1.0 so bloom catches them (and only them).
    gl_FragColor = vec4((col + t.b * vColor.rgb) * uBoost, a);
  } else {
    col = mix(col, uInk, t.g);
    col = mix(col, vec3(1.0), t.b * 0.85);
    gl_FragColor = vec4(col, a);
  }
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export const INK_HEX = '#2A1A3A';
/** Linear-HDR multiplier of the additive pool (bloom threshold is 1.1). */
export const ADDITIVE_BOOST = 1.9;

/** Size-over-life curves. */
export const Curve = { LINEAR: 0, POP: 1, HOLD: 2, GROW: 3 } as const;
export type Curve = (typeof Curve)[keyof typeof Curve];

/** A reusable spawn description; recipes fill `particles.spec` and call `emit`. */
export class ParticleSpec {
  x = 0; y = 0; z = 0;
  vx = 0; vy = 0; vz = 0;
  /** Downward acceleration (m/s²). */
  gravity = 0;
  /** Velocity damping per second (fraction lost per second, 0..∞ as exp rate). */
  drag = 0;
  life = 0.5;
  size = 0.3;
  sizeEnd = 0.3;
  curve: Curve = Curve.LINEAR;
  /** Height / width. */
  aspect = 1;
  rot = 0;
  spin = 0;
  r = 1; g = 1; b = 1;
  alpha = 1;
  /** Fraction of life after which the sprite fades out. */
  fadeAt = 0.6;
  cell: Cell = Cell.DOT;
  /** Velocity stretch (seconds of travel added to the sprite length). */
  stretch = 0;
  /** Confetti flutter frequency (Hz); 0 = no flutter. */
  flutter = 0;
  /** Floor height (three y) the particle bounces/settles on; -Infinity = none. */
  floor = Number.NEGATIVE_INFINITY;
  /** Restitution at the floor; negative = the particle vanishes when it comes down onto it. */
  bounce = 0.35;
  additive = false;
  /** Seconds before the sprite appears (it still moves meanwhile). */
  delay = 0;

  reset(): this {
    this.x = this.y = this.z = 0;
    this.vx = this.vy = this.vz = 0;
    this.gravity = 0;
    this.drag = 0;
    this.life = 0.5;
    this.size = this.sizeEnd = 0.3;
    this.curve = Curve.LINEAR;
    this.aspect = 1;
    this.rot = 0;
    this.spin = 0;
    this.r = this.g = this.b = 1;
    this.alpha = 1;
    this.fadeAt = 0.6;
    this.cell = Cell.DOT;
    this.stretch = 0;
    this.flutter = 0;
    this.floor = Number.NEGATIVE_INFINITY;
    this.bounce = 0.35;
    this.additive = false;
    this.delay = 0;
    return this;
  }

  color(c: Color): this {
    this.r = c.r;
    this.g = c.g;
    this.b = c.b;
    return this;
  }
}

/** One pool: CPU state plus the instanced mesh it uploads to. */
class Pool {
  readonly mesh: Mesh;
  count = 0;
  private readonly cap: number;
  // CPU state (struct of arrays)
  private readonly pos: Float32Array;
  private readonly vel: Float32Array;
  private readonly phys: Float32Array;   // gravity, drag, floor, bounce
  private readonly t: Float32Array;      // age, life
  private readonly size: Float32Array;   // size, sizeEnd, aspect, curve
  private readonly rot: Float32Array;    // rot, spin, flutter, phase
  private readonly col: Float32Array;    // r, g, b, alpha
  private readonly misc: Float32Array;   // cell, stretch, fadeAt, delay
  // GPU attributes
  private readonly aPos: InstancedBufferAttribute;
  private readonly aVel: InstancedBufferAttribute;
  private readonly aShape: InstancedBufferAttribute;
  private readonly aColor: InstancedBufferAttribute;
  private readonly aCell: InstancedBufferAttribute;
  private readonly geo: InstancedBufferGeometry;
  private readonly attrs: InstancedBufferAttribute[];

  constructor(cap: number, atlas: Texture, additive: boolean, name: string) {
    this.cap = cap;
    this.pos = new Float32Array(cap * 3);
    this.vel = new Float32Array(cap * 3);
    this.phys = new Float32Array(cap * 4);
    this.t = new Float32Array(cap * 2);
    this.size = new Float32Array(cap * 4);
    this.rot = new Float32Array(cap * 4);
    this.col = new Float32Array(cap * 4);
    this.misc = new Float32Array(cap * 4);
    const geo = new InstancedBufferGeometry();
    const quad = new PlaneGeometry(1, 1);
    geo.index = quad.index;
    geo.setAttribute('position', quad.getAttribute('position'));
    geo.setAttribute('uv', quad.getAttribute('uv'));
    const attr = (n: number): InstancedBufferAttribute => {
      const a = new InstancedBufferAttribute(new Float32Array(cap * n), n);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    this.aPos = attr(3);
    this.aVel = attr(3);
    this.aShape = attr(4);
    this.aColor = attr(4);
    this.aCell = attr(2);
    geo.setAttribute('iPos', this.aPos);
    geo.setAttribute('iVel', this.aVel);
    geo.setAttribute('iShape', this.aShape);
    geo.setAttribute('iColor', this.aColor);
    geo.setAttribute('iCell', this.aCell);
    geo.instanceCount = 0;
    this.geo = geo;
    this.attrs = [this.aPos, this.aVel, this.aShape, this.aColor, this.aCell];
    const mat = new ShaderMaterial({
      uniforms: {
        uAtlas: { value: atlas }, uGrid: { value: [ATLAS.cols, ATLAS.rows] }, uInk: { value: new Color(INK_HEX) },
        uAdditive: { value: additive ? 1 : 0 }, uBoost: { value: additive ? ADDITIVE_BOOST : 1 },
      },
      vertexShader: SPRITE_VERTEX,
      fragmentShader: SPRITE_FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending: additive ? AdditiveBlending : NormalBlending,
    });
    this.mesh = new Mesh(geo, mat);
    this.mesh.name = name;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 6 : 5;
  }

  emit(s: ParticleSpec): void {
    let i = this.count;
    if (i >= this.cap) {
      // Full: recycle the oldest-looking slot (highest age fraction) among a few probes.
      let best = 0;
      let bestF = -1;
      for (let k = 0; k < 8; k++) {
        const j = Math.floor(Math.random() * this.cap);
        const f = this.t[j * 2] / this.t[j * 2 + 1];
        if (f > bestF) {
          bestF = f;
          best = j;
        }
      }
      i = best;
    } else {
      this.count++;
    }
    const i2 = i * 2;
    const i3 = i * 3;
    const i4 = i * 4;
    this.pos[i3] = s.x;
    this.pos[i3 + 1] = s.y;
    this.pos[i3 + 2] = s.z;
    this.vel[i3] = s.vx;
    this.vel[i3 + 1] = s.vy;
    this.vel[i3 + 2] = s.vz;
    this.phys[i4] = s.gravity;
    this.phys[i4 + 1] = s.drag;
    this.phys[i4 + 2] = s.floor;
    this.phys[i4 + 3] = s.bounce;
    this.t[i2] = 0;
    this.t[i2 + 1] = Math.max(0.016, s.life);
    this.size[i4] = s.size;
    this.size[i4 + 1] = s.sizeEnd;
    this.size[i4 + 2] = s.aspect;
    this.size[i4 + 3] = s.curve;
    this.rot[i4] = s.rot;
    this.rot[i4 + 1] = s.spin;
    this.rot[i4 + 2] = s.flutter;
    this.rot[i4 + 3] = Math.random() * Math.PI * 2;
    this.col[i4] = s.r;
    this.col[i4 + 1] = s.g;
    this.col[i4 + 2] = s.b;
    this.col[i4 + 3] = s.alpha;
    this.misc[i4] = s.cell;
    this.misc[i4 + 1] = s.stretch;
    this.misc[i4 + 2] = s.fadeAt;
    this.misc[i4 + 3] = s.delay;
    // The delay extends the life so the visible part keeps its length.
    this.t[i2 + 1] += s.delay;
  }

  /** One-frame sprites (state-driven: dizzy stars…): count and their packed GPU values. */
  private imm = 0;
  private readonly immCap = 64;
  private readonly immData = new Float32Array(64 * 16);

  /** A sprite drawn this frame only (positions are three.js world). */
  sprite(s: ParticleSpec): void {
    if (this.imm >= this.immCap) return;
    const d = this.immData;
    const o = this.imm * 16;
    d[o] = s.x; d[o + 1] = s.y; d[o + 2] = s.z;
    d[o + 3] = s.vx; d[o + 4] = s.vy; d[o + 5] = s.vz;
    d[o + 6] = s.size; d[o + 7] = s.size * s.aspect; d[o + 8] = s.rot; d[o + 9] = s.stretch;
    d[o + 10] = s.r; d[o + 11] = s.g; d[o + 12] = s.b; d[o + 13] = s.alpha;
    d[o + 14] = s.cell; d[o + 15] = 1;
    this.imm++;
  }

  /** Integrates every live particle by `dt` seconds. */
  integrate(dt: number): void {
    let n = this.count;
    const P = this.pos;
    const V = this.vel;
    for (let i = 0; i < n; ) {
      const i2 = i * 2;
      const age = this.t[i2] + dt;
      if (age >= this.t[i2 + 1]) {
        n--;
        if (i !== n) this.move(n, i);
        continue;
      }
      this.t[i2] = age;
      const i3 = i * 3;
      const i4 = i * 4;
      const drag = this.phys[i4 + 1];
      if (drag > 0) {
        const k = Math.exp(-drag * dt);
        V[i3] *= k;
        V[i3 + 1] *= k;
        V[i3 + 2] *= k;
      }
      V[i3 + 1] -= this.phys[i4] * dt;
      P[i3] += V[i3] * dt;
      P[i3 + 1] += V[i3 + 1] * dt;
      P[i3 + 2] += V[i3 + 2] * dt;
      const floor = this.phys[i4 + 2];
      if (P[i3 + 1] < floor) {
        P[i3 + 1] = floor;
        if (this.phys[i4 + 3] < 0) {
          // A negative bounce sinks on contact (droplets back into the liquid): it dies now.
          if (V[i3 + 1] < 0) this.t[i2] = this.t[i2 + 1];
        } else if (V[i3 + 1] < 0) {
          V[i3 + 1] *= -this.phys[i4 + 3];
          V[i3] *= 0.6;
          V[i3 + 2] *= 0.6;
          this.rot[i4 + 1] *= 0.5;
          this.rot[i4 + 2] *= 0.3;
        }
      }
      this.rot[i4] += this.rot[i4 + 1] * dt;
      i++;
    }
    this.count = n;
  }

  /** Uploads the frame (timed particles plus this frame's sprites). */
  flush(): void {
    this.upload();
    this.imm = 0;
  }

  clear(): void {
    this.count = 0;
    this.imm = 0;
    this.geo.instanceCount = 0;
  }

  private move(from: number, to: number): void {
    const f2 = from * 2;
    const f3 = from * 3;
    const f4 = from * 4;
    this.pos.copyWithin(to * 3, f3, f3 + 3);
    this.vel.copyWithin(to * 3, f3, f3 + 3);
    this.misc.copyWithin(to * 4, f4, f4 + 4);
    this.phys.copyWithin(to * 4, f4, f4 + 4);
    this.size.copyWithin(to * 4, f4, f4 + 4);
    this.rot.copyWithin(to * 4, f4, f4 + 4);
    this.col.copyWithin(to * 4, f4, f4 + 4);
    this.t.copyWithin(to * 2, f2, f2 + 2);
  }

  private upload(): void {
    const n = this.count;
    const ap = this.aPos.array as Float32Array;
    const av = this.aVel.array as Float32Array;
    const as = this.aShape.array as Float32Array;
    const ac = this.aColor.array as Float32Array;
    const ak = this.aCell.array as Float32Array;
    for (let i = 0; i < n; i++) {
      const i2 = i * 2;
      const i3 = i * 3;
      const i4 = i * 4;
      const delay = this.misc[i4 + 3];
      const f = Math.max(0, this.t[i2] - delay) / Math.max(0.001, this.t[i2 + 1] - delay);
      const hidden = this.t[i2] < delay;
      const s0 = this.size[i4];
      const s1 = this.size[i4 + 1];
      let s: number;
      switch (this.size[i4 + 3]) {
        case Curve.POP: s = f < 0.15 ? s0 * (f / 0.15) * 1.15 : s0 + (s1 - s0) * ((f - 0.15) / 0.85); break;
        case Curve.HOLD: s = f < 0.7 ? s0 : s0 + (s1 - s0) * ((f - 0.7) / 0.3); break;
        case Curve.GROW: s = s0 + (s1 - s0) * (1 - (1 - f) * (1 - f)); break;
        default: s = s0 + (s1 - s0) * f;
      }
      const fadeAt = this.misc[i4 + 2];
      const fade = f < fadeAt ? 1 : 1 - (f - fadeAt) / Math.max(0.001, 1 - fadeAt);
      ap[i3] = this.pos[i3];
      ap[i3 + 1] = this.pos[i3 + 1];
      ap[i3 + 2] = this.pos[i3 + 2];
      av[i3] = this.vel[i3];
      av[i3 + 1] = this.vel[i3 + 1];
      av[i3 + 2] = this.vel[i3 + 2];
      as[i4] = s;
      as[i4 + 1] = s * this.size[i4 + 2];
      as[i4 + 2] = this.rot[i4];
      as[i4 + 3] = this.misc[i4 + 1];
      ac[i4] = this.col[i4];
      ac[i4 + 1] = this.col[i4 + 1];
      ac[i4 + 2] = this.col[i4 + 2];
      ac[i4 + 3] = hidden ? 0 : this.col[i4 + 3] * fade;
      ak[i2] = this.misc[i4];
      const fl = this.rot[i4 + 2];
      ak[i2 + 1] = fl > 0 ? Math.cos(this.rot[i4 + 3] + this.t[i2] * fl * Math.PI * 2) : 1;
    }
    let total = n;
    const d = this.immData;
    for (let j = 0; j < this.imm && total < this.cap; j++, total++) {
      const o = j * 16;
      const i2 = total * 2;
      const i3 = total * 3;
      const i4 = total * 4;
      ap[i3] = d[o]; ap[i3 + 1] = d[o + 1]; ap[i3 + 2] = d[o + 2];
      av[i3] = d[o + 3]; av[i3 + 1] = d[o + 4]; av[i3 + 2] = d[o + 5];
      as[i4] = d[o + 6]; as[i4 + 1] = d[o + 7]; as[i4 + 2] = d[o + 8]; as[i4 + 3] = d[o + 9];
      ac[i4] = d[o + 10]; ac[i4 + 1] = d[o + 11]; ac[i4 + 2] = d[o + 12]; ac[i4 + 3] = d[o + 13];
      ak[i2] = d[o + 14]; ak[i2 + 1] = d[o + 15];
    }
    this.geo.instanceCount = total;
    if (total === 0) return;
    for (let k = 0; k < this.attrs.length; k++) {
      const a = this.attrs[k];
      a.clearUpdateRanges();
      a.addUpdateRange(0, total * a.itemSize);
      a.needsUpdate = true;
    }
  }
}

/** Budget split between the toon pool and the additive pool. */
const ADDITIVE_SHARE = 0.3;

export class ParticleSystem {
  readonly root = new Group();
  /** The reusable spawn description. */
  readonly spec = new ParticleSpec();
  private normal: Pool;
  private additive: Pool;
  private readonly atlas: Texture;
  private budget: number;

  constructor(atlas: Texture, budget: number) {
    this.atlas = atlas;
    this.budget = budget;
    this.root.name = 'particles';
    this.normal = new Pool(1, atlas, false, 'particles:toon');
    this.additive = new Pool(1, atlas, true, 'particles:glow');
    this.build(budget);
  }

  /** Additive HDR boost: > 1 only where a bloom pass turns it into glow (High, Medium). */
  setBoost(k: number): void {
    this.boost = k;
    ((this.additive.mesh.material as ShaderMaterial).uniforms.uBoost as { value: number }).value = k;
  }

  private boost = ADDITIVE_BOOST;

  /** Total particle budget (§12.9 per tier); rebuilding drops the live particles. */
  setBudget(n: number): void {
    if (n === this.budget) return;
    this.budget = n;
    this.build(n);
  }

  get count(): number {
    return this.normal.count + this.additive.count;
  }

  get capacity(): number {
    return this.budget;
  }

  emit(s: ParticleSpec = this.spec): void {
    (s.additive ? this.additive : this.normal).emit(s);
  }

  /** Starts a frame: integrates the live particles. */
  begin(dt: number): void {
    this.normal.integrate(dt);
    this.additive.integrate(dt);
  }

  /** A sprite for this frame only (state-driven effects such as orbiting Dizzy stars). */
  sprite(s: ParticleSpec = this.spec): void {
    (s.additive ? this.additive : this.normal).sprite(s);
  }

  /** Ends the frame: uploads the particles and this frame's sprites. */
  end(): void {
    this.normal.flush();
    this.additive.flush();
  }

  clear(): void {
    this.normal.clear();
    this.additive.clear();
  }

  private build(n: number): void {
    for (const p of [this.normal, this.additive]) {
      this.root.remove(p.mesh);
      p.mesh.geometry.dispose();
      (p.mesh.material as ShaderMaterial).dispose();
    }
    const add = Math.max(16, Math.round(n * ADDITIVE_SHARE));
    this.normal = new Pool(Math.max(16, n - add), this.atlas, false, 'particles:toon');
    this.additive = new Pool(add, this.atlas, true, 'particles:glow');
    ((this.additive.mesh.material as ShaderMaterial).uniforms.uBoost as { value: number }).value = this.boost;
    this.root.add(this.normal.mesh, this.additive.mesh);
  }
}
